import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ExternalLink, MessageCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { ARTICLES } from "@/data/articles";
import { IMG_KASO_SUPER, LOGO_URL, navigateTo, waLink } from "@/data/site";

const DEFAULT_TITLE = "Jual Kayu & Triplek Murah Jakarta | PT. SINAR AL MUBAROKAH";
const DEFAULT_DESC =
  "Distributor kayu olahan & triplek harga pabrik: kaso, balok, multiplek, dolken gelam. Stok melimpah, legal SVLK, kirim se-Jabodetabek. WhatsApp 0822-8280-0020.";

const setMeta = (a) => {
  document.title = a ? a.title : DEFAULT_TITLE;
  const d = document.querySelector('meta[name="description"]');
  if (d) d.setAttribute("content", a ? a.metaDescription : DEFAULT_DESC);
  const c = document.querySelector('link[rel="canonical"]');
  if (c)
    c.setAttribute(
      "href",
      a ? `https://pt.sinaralmubarokah.com/artikel/${a.slug}` : "https://pt.sinaralmubarokah.com/",
    );
};

export default function ArticlePage({ slug }) {
  const [article, setArticle] = useState(() => ARTICLES.find((x) => x.slug === slug) || null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      setLoaded(true);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles/auto`)
      .then((r) => r.json())
      .then((d) => setArticle((d.articles || []).find((x) => x.slug === slug) || null))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [slug]);

  useEffect(() => {
    if (article) setMeta(article);
    return () => setMeta(null);
  }, [article]);

  if (loaded && !article) {
    return (
      <div className="surface flex min-h-screen flex-col">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-6 pt-28 pb-20 text-center" data-testid="article-not-found">
          <h1 className="font-serif text-3xl font-semibold text-brand-ink sm:text-4xl">Artikel Tidak Ditemukan</h1>
          <p className="mt-4 text-brand-muted">Tautan artikel ini mungkin sudah berubah.</p>
          <button
            onClick={() => navigateTo("/")}
            data-testid="article-back-home"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold"
            style={{ backgroundColor: "var(--t-accent)", color: "var(--t-dark)" }}
          >
            <ArrowLeft size={16} /> Kembali ke Beranda
          </button>
        </main>
        <Footer />
      </div>
    );
  }
  if (!article) return null;

  const img = article.image || IMG_KASO_SUPER;
  const wiki = article.wiki || { label: "Mengenal material kayu — Wikipedia", url: "https://id.wikipedia.org/wiki/Kayu" };
  const internal = article.internal || { label: "Lihat katalog produk kayu & triplek", href: "#katalog" };

  return (
    <div className="surface min-h-screen" data-testid="article-page">
      <Navbar />
      <main className="pt-24">
        <div className="relative">
          <img src={img} alt={article.keyword} className="h-64 w-full object-cover sm:h-96" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--t-bg) 4%, transparent 55%)" }} />
        </div>
        <article className="mx-auto max-w-3xl px-6 pb-24">
          <button
            onClick={() => navigateTo("/#artikel")}
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-muted hover:underline"
            data-testid="article-back-button"
          >
            <ArrowLeft size={15} /> Semua Artikel
          </button>
          <p className="overline-label">{article.keyword}</p>
          <h1 className="mt-4 font-serif text-3xl font-semibold leading-tight text-brand-ink sm:text-4xl lg:text-5xl">
            {article.title}
          </h1>
          <div className="mt-5 flex items-center gap-3 border-b pb-6" style={{ borderColor: "var(--t-border)" }}>
            <img src={LOGO_URL} alt="PT. SINAR AL MUBAROKAH" className="h-9 w-9 rounded-lg object-cover" />
            <p className="text-xs font-semibold text-brand-muted">
              Ditulis oleh <span className="accent-text">PT. SINAR AL MUBAROKAH</span>
              {article.publishedAt && ` — ${new Date(article.publishedAt).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`}
            </p>
          </div>
          {article.sections.map((s, i) => (
            <section key={i} className="mt-9">
              <h2 className="font-serif text-xl font-semibold text-brand-ink sm:text-2xl">{s.h}</h2>
              {s.paras.map((p, j) => (
                <p key={j} className="mt-3 text-sm leading-relaxed text-brand-muted sm:text-base">
                  {p}
                </p>
              ))}
            </section>
          ))}

          <div className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--t-border)" }} data-testid="article-links">
            <p className="overline-label mb-4">Referensi & Bacaan Lanjut</p>
            <a
              href={wiki.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-wiki-link"
              className="flex items-center gap-2 text-sm font-semibold hover:underline"
              style={{ color: "var(--t-accent)" }}
            >
              <ExternalLink size={15} /> {wiki.label}
            </a>
            <a
              href={`/${internal.href}`}
              data-testid="article-internal-link"
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-brand-muted hover:underline"
            >
              <ArrowRight size={15} /> {internal.label}
            </a>
          </div>

          <a
            href={waLink(`Halo PT. SINAR AL MUBAROKAH, saya membaca artikel "${article.title}" dan ingin menanyakan harga & stok. Mohon info penawarannya.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="article-whatsapp-cta"
            className="mt-10 flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-bold transition-transform duration-300 hover:-translate-y-0.5"
            style={{ backgroundColor: "var(--t-accent)", color: "var(--t-dark)" }}
          >
            <MessageCircle size={17} /> Tanya Harga via WhatsApp
          </a>
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
